import { z } from 'zod';
import {
    type CloverCnpTokenSaleAdditionalCharge,
    CloverCnpTokenSaleAdditionalChargeType,
    CloverCnpTokenSaleNetworkStatus,
    CloverCnpTokenSaleOutcome,
    CloverCnpTokenSaleOutcomeType,
    type CloverCnpTokenSaleApprovedChargeBody,
    type CloverCnpTokenSaleApprovedResponseDetails,
    type CloverCnpTokenSaleCardDeclinedBody,
    type CloverCnpTokenSaleCardDeclinedResponseDetails,
    type CloverCnpTokenSaleChargeBody,
    type CloverCnpTokenSaleChargeOutcomeBody,
    type CloverCnpTokenSaleDeclinedChargeBody,
    type CloverCnpTokenSaleDeclinedResponseDetails,
    type CloverCnpTokenSaleFirstStoredCredentials,
    type CloverCnpTokenSaleInvalidSourceBody,
    type CloverCnpTokenSaleInvalidSourceResponseDetails,
    type CloverCnpTokenSaleMalformedResponseDetails,
    type CloverCnpTokenSaleRequestBody,
    type CloverCnpTokenSaleRequestDetails,
    type CloverCnpTokenSaleRequestHeaders,
    type CloverCnpTokenSaleResponseDetails,
    type CloverCnpTokenSaleSourceBody,
    CloverCnpTokenSaleStatus,
    type CloverCnpTokenSaleStoredCredentials,
    type CloverCnpTokenSaleSubsequentStoredCredentials,
} from '../../../types/ipg/clover/clover-cnp-token-sale.types';

export const CloverCnpTokenSaleAmountSchema: z.ZodNumber = z.number().int().min(0).max(999999999);

export const CloverCnpTokenSaleCurrencySchema: z.ZodString = z.string().regex(/^[a-z]{3}$/);

export const CloverCnpTokenSaleRequestHeadersSchema: z.ZodObject<{
    userAgent: z.ZodString;
    forwardedFor: z.ZodString;
}> = z.object({
    userAgent: z.string().min(1),
    forwardedFor: z.string().min(1),
}) satisfies z.ZodType<CloverCnpTokenSaleRequestHeaders>;

export const CloverCnpTokenSaleFirstStoredCredentialsSchema: z.ZodObject<{
    sequence: z.ZodLiteral<'FIRST'>;
    is_scheduled: z.ZodLiteral<false>;
    initiator: z.ZodLiteral<'CARDHOLDER'>;
}> = z
    .object({
        sequence: z.literal('FIRST'),
        is_scheduled: z.literal(false),
        initiator: z.literal('CARDHOLDER'),
    })
    .strict() satisfies z.ZodType<CloverCnpTokenSaleFirstStoredCredentials>;

export const CloverCnpTokenSaleSubsequentStoredCredentialsSchema: z.ZodObject<{
    sequence: z.ZodLiteral<'SUBSEQUENT'>;
    is_scheduled: z.ZodOptional<z.ZodBoolean>;
    initiator: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'MERCHANT'>, z.ZodLiteral<'CARDHOLDER'>]>
    >;
}> = z
    .object({
        sequence: z.literal('SUBSEQUENT'),
        is_scheduled: z.boolean().optional(),
        initiator: z.union([z.literal('MERCHANT'), z.literal('CARDHOLDER')]).optional(),
    })
    .strict() satisfies z.ZodType<CloverCnpTokenSaleSubsequentStoredCredentials>;

export const CloverCnpTokenSaleStoredCredentialsSchema: z.ZodType<CloverCnpTokenSaleStoredCredentials> =
    z.discriminatedUnion('sequence', [
        CloverCnpTokenSaleFirstStoredCredentialsSchema,
        CloverCnpTokenSaleSubsequentStoredCredentialsSchema,
    ]);

export const CloverCnpTokenSaleRequestBodySchema: z.ZodObject<{
    amount: typeof CloverCnpTokenSaleAmountSchema;
    currency: typeof CloverCnpTokenSaleCurrencySchema;
    source: z.ZodString;
    intent: z.ZodOptional<z.ZodLiteral<'save_credential_on_file'>>;
    capture: z.ZodOptional<z.ZodBoolean>;
    description: z.ZodOptional<z.ZodString>;
    external_reference_id: z.ZodOptional<z.ZodString>;
    receipt_email: z.ZodOptional<z.ZodString>;
    stored_credentials: z.ZodOptional<typeof CloverCnpTokenSaleStoredCredentialsSchema>;
}> = z.object({
    amount: CloverCnpTokenSaleAmountSchema,
    currency: CloverCnpTokenSaleCurrencySchema,
    source: z.string().min(1),
    intent: z.literal('save_credential_on_file').optional(),
    capture: z.boolean().optional(),
    description: z.string().min(1).max(255).optional(),
    external_reference_id: z.string().min(1).max(12).optional(),
    receipt_email: z.string().min(1).optional(),
    stored_credentials: CloverCnpTokenSaleStoredCredentialsSchema.optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleRequestBody>;

export const CloverCnpTokenSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'POST'>;
    path: z.ZodLiteral<'/v1/charges'>;
    headers: typeof CloverCnpTokenSaleRequestHeadersSchema;
    body: typeof CloverCnpTokenSaleRequestBodySchema;
}> = z.object({
    method: z.literal('POST'),
    path: z.literal('/v1/charges'),
    headers: CloverCnpTokenSaleRequestHeadersSchema,
    body: CloverCnpTokenSaleRequestBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleRequestDetails>;

export const CloverCnpTokenSaleNetworkStatusSchema: z.ZodType<CloverCnpTokenSaleNetworkStatus> =
    z.enum(CloverCnpTokenSaleNetworkStatus);

export const CloverCnpTokenSaleOutcomeTypeSchema: z.ZodType<CloverCnpTokenSaleOutcomeType> = z.enum(
    CloverCnpTokenSaleOutcomeType
);

export const CloverCnpTokenSaleStatusSchema: z.ZodType<CloverCnpTokenSaleStatus> =
    z.enum(CloverCnpTokenSaleStatus);

export const CloverCnpTokenSaleChargeOutcomeBodySchema: z.ZodObject<{
    network_status: z.ZodOptional<typeof CloverCnpTokenSaleNetworkStatusSchema>;
    reason: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    seller_message: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<typeof CloverCnpTokenSaleOutcomeTypeSchema>;
}> = z.object({
    network_status: CloverCnpTokenSaleNetworkStatusSchema.optional(),
    reason: z.string().min(1).nullable().optional(),
    seller_message: z.string().min(1).optional(),
    type: CloverCnpTokenSaleOutcomeTypeSchema.optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleChargeOutcomeBody>;

export const CloverCnpTokenSaleSourceBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    object: z.ZodOptional<z.ZodString>;
    brand: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    exp_month: z.ZodOptional<z.ZodString>;
    exp_year: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    funding: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
    object: z.string().min(1).optional(),
    brand: z.string().min(1).nullable().optional(),
    exp_month: z.string().min(1).optional(),
    exp_year: z.string().min(1).optional(),
    first6: z.string().min(1).optional(),
    funding: z.string().min(1).optional(),
    last4: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverCnpTokenSaleSourceBody>;

export const CloverCnpTokenSaleAdditionalChargeSchema: z.ZodObject<{
    amount: z.ZodNumber;
    id: z.ZodOptional<z.ZodString>;
    rate: z.ZodOptional<z.ZodNumber>;
    type: z.ZodLiteral<typeof CloverCnpTokenSaleAdditionalChargeType.CREDIT_SURCHARGE>;
}> = z
    .object({
        amount: z.number().int().min(0).max(999999999),
        id: z.string().min(1).optional(),
        rate: z.number().int().min(0).optional(),
        type: z.literal(CloverCnpTokenSaleAdditionalChargeType.CREDIT_SURCHARGE),
    })
    .strict() satisfies z.ZodType<CloverCnpTokenSaleAdditionalCharge>;

export const CloverCnpTokenSaleChargeBodySchema: z.ZodObject<{
    id: z.ZodString;
    amount: z.ZodOptional<typeof CloverCnpTokenSaleAmountSchema>;
    additional_charges: z.ZodOptional<typeof CloverCnpTokenSaleAdditionalChargeSchema>;
    amount_refunded: z.ZodOptional<z.ZodNumber>;
    auth_code: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    captured: z.ZodBoolean;
    created: z.ZodOptional<z.ZodNumber>;
    currency: typeof CloverCnpTokenSaleCurrencySchema;
    description: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    external_reference_id: z.ZodOptional<z.ZodString>;
    failure_code: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    failure_message: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    outcome: z.ZodOptional<typeof CloverCnpTokenSaleChargeOutcomeBodySchema>;
    paid: z.ZodBoolean;
    receipt_email: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    ref_num: z.ZodOptional<z.ZodNullable<z.ZodString>>;
    refunded: z.ZodOptional<z.ZodBoolean>;
    source: z.ZodOptional<typeof CloverCnpTokenSaleSourceBodySchema>;
    status: typeof CloverCnpTokenSaleStatusSchema;
}> = z.object({
    id: z.string().min(1),
    amount: CloverCnpTokenSaleAmountSchema.optional(),
    additional_charges: CloverCnpTokenSaleAdditionalChargeSchema.optional(),
    amount_refunded: z.number().int().min(0).optional(),
    auth_code: z.string().min(1).nullable().optional(),
    captured: z.boolean(),
    created: z.number().int().min(0).optional(),
    currency: CloverCnpTokenSaleCurrencySchema,
    description: z.string().min(1).nullable().optional(),
    external_reference_id: z.string().min(1).max(12).optional(),
    failure_code: z.string().min(1).nullable().optional(),
    failure_message: z.string().min(1).nullable().optional(),
    outcome: CloverCnpTokenSaleChargeOutcomeBodySchema.optional(),
    paid: z.boolean(),
    receipt_email: z.string().min(1).nullable().optional(),
    ref_num: z.string().min(1).nullable().optional(),
    refunded: z.boolean().optional(),
    source: CloverCnpTokenSaleSourceBodySchema.optional(),
    status: CloverCnpTokenSaleStatusSchema,
}) satisfies z.ZodType<CloverCnpTokenSaleChargeBody>;

export const CloverCnpTokenSaleApprovedChargeBodySchema: z.ZodType<CloverCnpTokenSaleApprovedChargeBody> =
    CloverCnpTokenSaleChargeBodySchema.extend({
        captured: z.literal(true),
        paid: z.literal(true),
        status: z.literal(CloverCnpTokenSaleStatus.SUCCEEDED),
    });

export const CloverCnpTokenSaleDeclinedChargeBodySchema: z.ZodType<CloverCnpTokenSaleDeclinedChargeBody> =
    CloverCnpTokenSaleChargeBodySchema.extend({
        paid: z.literal(false),
        status: z.literal(CloverCnpTokenSaleStatus.FAILED),
    });

export const CloverCnpTokenSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverCnpTokenSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverCnpTokenSaleApprovedChargeBodySchema;
}> = z.object({
    outcome: z.literal(CloverCnpTokenSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: CloverCnpTokenSaleApprovedChargeBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleApprovedResponseDetails>;

export const CloverCnpTokenSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverCnpTokenSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverCnpTokenSaleDeclinedChargeBodySchema;
}> = z.object({
    outcome: z.literal(CloverCnpTokenSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: CloverCnpTokenSaleDeclinedChargeBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleDeclinedResponseDetails>;

export const CloverCnpTokenSaleCardDeclinedBodySchema: z.ZodObject<{
    error: z.ZodObject<{
        code: z.ZodLiteral<'card_declined'>;
        charge: z.ZodString;
        message: z.ZodString;
        declineCode: z.ZodLiteral<typeof CloverCnpTokenSaleOutcomeType.ISSUER_DECLINED>;
    }>;
    message: z.ZodLiteral<'402 Payment Required'>;
}> = z
    .object({
        error: z
            .object({
                code: z.literal('card_declined'),
                charge: z.string().min(1),
                message: z.string().min(1),
                declineCode: z.literal(CloverCnpTokenSaleOutcomeType.ISSUER_DECLINED),
            })
            .strict(),
        message: z.literal('402 Payment Required'),
    })
    .strict() satisfies z.ZodType<CloverCnpTokenSaleCardDeclinedBody>;

export const CloverCnpTokenSaleCardDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverCnpTokenSaleOutcome.CARD_DECLINED>;
    httpStatus: z.ZodLiteral<402>;
    body: typeof CloverCnpTokenSaleCardDeclinedBodySchema;
}> = z.object({
    outcome: z.literal(CloverCnpTokenSaleOutcome.CARD_DECLINED),
    httpStatus: z.literal(402),
    body: CloverCnpTokenSaleCardDeclinedBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleCardDeclinedResponseDetails>;

export const CloverCnpTokenSaleInvalidSourceBodySchema: z.ZodObject<{
    error: z.ZodObject<{
        code: z.ZodLiteral<'processing_error'>;
        message: z.ZodLiteral<'Invalid Request. Pay request sent for stored credential transaction with invalid source.'>;
    }>;
    message: z.ZodLiteral<'400 Bad Request'>;
}> = z
    .object({
        error: z
            .object({
                code: z.literal('processing_error'),
                message: z.literal(
                    'Invalid Request. Pay request sent for stored credential transaction with invalid source.'
                ),
            })
            .strict(),
        message: z.literal('400 Bad Request'),
    })
    .strict() satisfies z.ZodType<CloverCnpTokenSaleInvalidSourceBody>;

export const CloverCnpTokenSaleInvalidSourceResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverCnpTokenSaleOutcome.INVALID_SOURCE>;
    httpStatus: z.ZodLiteral<400>;
    body: typeof CloverCnpTokenSaleInvalidSourceBodySchema;
}> = z.object({
    outcome: z.literal(CloverCnpTokenSaleOutcome.INVALID_SOURCE),
    httpStatus: z.literal(400),
    body: CloverCnpTokenSaleInvalidSourceBodySchema,
}) satisfies z.ZodType<CloverCnpTokenSaleInvalidSourceResponseDetails>;

export const CloverCnpTokenSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverCnpTokenSaleOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverCnpTokenSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CloverCnpTokenSaleMalformedResponseDetails>;

export const CloverCnpTokenSaleResponseDetailsSchema: z.ZodType<CloverCnpTokenSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverCnpTokenSaleApprovedResponseDetailsSchema,
        CloverCnpTokenSaleCardDeclinedResponseDetailsSchema,
        CloverCnpTokenSaleDeclinedResponseDetailsSchema,
        CloverCnpTokenSaleInvalidSourceResponseDetailsSchema,
        CloverCnpTokenSaleMalformedResponseDetailsSchema,
    ]);
